<script lang="ts" setup>
import {
  Component,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import { useEvent } from "vue-composable";
import { dispatch } from "@/utils/bus";
import { useMenuPosition } from "@/composables/useMenuPosition";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    noAutoOpen?: boolean;
    blockDispatch?: boolean;
    target?: Record<string, any> | HTMLElement | string | Component | null;
    renderTarget?: string | HTMLElement | Component | null;
    offset?: [left: number, top: number];
    maxHeight?: number;
    limitWidthToTarget?: boolean; // if menu is really long make sure its scrolled to top item
    resetScroll?: boolean;
    id?: string;
    noMouseleaveClose?: boolean; // ENG-21051 search selects should not close on mouseleave
  }>(),
  { resetScroll: true },
);

const emit = defineEmits({
  "update:modelValue": (_value: boolean) => true,
  blur: (_e: FocusEvent) => true,
  focus: (_e: FocusEvent) => true,
});

provide("closeMenu", () => {
  show.value = false;
});

const menu = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);

const __count = ref(0);
const { proxy, parent } = getCurrentInstance()!;

const target = computed(() => {
  // just having something to trigger the computed
  __count.value;
  const target = props.target
    ? typeof props.target === "string"
      ? (document.querySelector(props.target) as HTMLElement)
      : (((props.target as any).$el || props.target) as HTMLElement)
    : (parent?.proxy?.$el as HTMLElement);

  if (target === proxy?.$el) {
    return parent?.proxy?.$parent?.$el as HTMLElement;
  }
  return target;
});

const elements = [target, menu];

const { position, updatePosition, isInsideDragModal } = useMenuPosition(
  {
    offset: props.offset,
    maxHeight: props.maxHeight,
  },
  target,
  content,
  menu,
);

const positionTop = computed(() => position.value.top);
const positionLeft = computed(() => position.value.left);
const positionWidth = computed(() => position.value.width);
const positionHeight = computed(() => position.value.height);

const show = ref(props.modelValue ?? false);

// ENG-21695 vue-composable bundles a different vue copy; its Ref symbol differs from ours
useEvent(target as any, "focusin", () => {
  if (props.noAutoOpen) return;
  show.value = true;
});

function handleMenuFocus() {
  if (
    document.activeElement === target.value ||
    document.activeElement === proxy?.$el ||
    (proxy?.$el as HTMLElement).contains(document.activeElement)
  ) {
    return;
  }

  if (!props.blockDispatch) {
    dispatch("close-menus", { target: props.id });
    return (show.value = false);
  }
}

let timer: ReturnType<typeof setTimeout>;
function handleMouseEnter() {
  clearTimeout(timer);
}

function handleMouseLeave(e: MouseEvent) {
  if (props.noMouseleaveClose) return; // ENG-21051

  const next = e.relatedTarget as Node | null;

  const stillInside = elements.some((ref) => ref.value?.contains(next));
  if (stillInside) return;

  timer = setTimeout(function () {
    if (!props.blockDispatch) {
      dispatch("close-menus", { target: props.id });
    }
    show.value = false;
  }, 300);
}

function resetMenuScroll() {
  const el = content.value;
  if (props.resetScroll && el && el?.scrollTop > 0) {
    el.scrollTo(0, 0);
  }
}

watch(show, async (s) => {
  emit("update:modelValue", s);
  updatePosition();
  await nextTick();
  resetMenuScroll();
});

watch(
  menu,
  (v, _oldValue, onCleanup) => {
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);
    onCleanup(() => {
      window.clearInterval(id);
    });
  },
  { flush: "sync" },
);

watch(content, (v) => {
  if (v) {
    updatePosition();
  }
});

watch(
  () => props.modelValue,
  (v, _oldValue, onCleanup) => {
    show.value = v;
    if (!v) return;
    updatePosition();
    const id = window.setInterval(() => {
      updatePosition();
    }, 100);
    if (isInsideDragModal.value) {
      elements.forEach((elRef) => {
        if (elRef.value) {
          elRef.value.addEventListener("mouseleave", handleMouseLeave);
        }
      });
    }
    onCleanup(() => {
      window.clearInterval(id);
      elements.forEach((elRef) => {
        if (elRef.value) {
          elRef.value.removeEventListener("mouseleave", handleMouseLeave);
        }
      });
    });
  },
);

onMounted(() => {
  __count.value++;
  window.addEventListener("focus", handleMenuFocus, true);
  menu.value?.parentElement?.addEventListener("mouseenter", handleMouseEnter);
  menu.value?.parentElement?.addEventListener("mouseleave", handleMouseLeave);
});

onUnmounted(() => {
  window.removeEventListener("focus", handleMenuFocus, true);
  menu.value?.parentElement?.removeEventListener("mouseenter", handleMouseEnter);
  menu.value?.parentElement?.removeEventListener("mouseleave", handleMouseLeave);
  clearTimeout(timer);
  elements.forEach((elRef) => {
    if (elRef.value) {
      elRef.value.removeEventListener("mouseleave", handleMouseLeave);
    }
  });
});

defineExpose({
  hide() {
    show.value = false;
  },
  show() {
    show.value = true;
  },
});
</script>
<template>
  <teleport v-if="renderTarget" :to="renderTarget">
    <div
      v-if="show"
      :id="id"
      ref="menu"
      class="simple-menu"
      :class="{
        limitWidthToTarget,
      }"
      tabindex="-1"
      @focus="$emit('focus', $event)"
      @mouseleave="handleMouseLeave($event)"
    >
      <div ref="content" class="simple-menu--content">
        <slot />
      </div>
    </div>
  </teleport>

  <div
    v-else-if="show"
    :id="id"
    ref="menu"
    class="simple-menu"
    :class="{
      limitWidthToTarget,
    }"
    tabindex="-1"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <div ref="content" class="simple-menu--content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss">
.simple-menu {
  animation: fadeIn 0.1s ease both;
  animation-delay: 50ms;

  position: fixed;
  z-index: 99999;

  top: v-bind(positionTop);
  left: v-bind(positionLeft);
  min-width: v-bind(positionWidth);
  max-height: v-bind(positionHeight);

  will-change: auto;
  transition: display 300ms ease-in-out;
  box-shadow:
    0px 4px 6px var(--shadow-colour-subtle),
    0px 2px 4px var(--shadow-colour-subtle);

  background-color: var(--surface-default);

  border-radius: var(--radius-default);
  overflow: hidden;

  display: flex;

  .simple-menu--content {
    flex: 1 1 auto;
    overflow: auto;
    width: auto;
  }

  &.limitWidthToTarget {
    max-width: v-bind(positionWidth);
  }
}
</style>
